import {
  loginHasDirectBusiness,
  loginIsCommercialChannelPartner,
  loginIsCommercialEuChannelPartner,
} from "@/utils/directBusiness";
import {useEffect, useMemo, useState} from "react";

import {FeatureKey} from "@/types/generated/FeatureKey";
import {hasFeature} from "@hooks/useFeatures";
import {UserPermission} from "@/types/generated/UserPermission";
import {hasPermission} from "@hooks/usePermissions";
import {setSideBarColors} from "@/containers/Theme/ThemeHelper";

import organizationClient from "@apis/organizationClient";

import useAvailibleMenu from "./useAvailibleMenu";
import useAgentMenuItems, { AGENT_GROUP_STATE } from "./useAgentMenuItems";
import useDashboardMenuItems, {DASHBOARD_GROUP_STATE} from "./useDashboardMenuItems";
import {
  type AvailibleMenuData,
  type MenuRoute,
  type MenuType,
  getEnhancedOrgType,
  MenuSourceConst,
  MenuTypeConst,
  OrgTypeConst,
} from "./menuTypes";
import {
  MenuLevelConst,
  OWN_LEVEL_DETAIL_ROUTES,
  type MenuLevel,
} from "@/contexts/OrgContext";
import {isHomeMenuReachable} from "./useNavigationModel";
import type {LoginInfo, LoginOrganisationInfo, Partner} from "@/types/LoginInfo";
import {LanguageKey} from "@/types/generated/LanguageKey";
import {translate} from "@hooks/useLanguage";
import { canSeeAtOwnLevel } from "@/utils/partnerLevelFeature";

/**
 * Extended organization info that includes partner data from API response
 */
interface ExtendedOrganisationInfo extends LoginOrganisationInfo {
  partner?: Partner;
}

/**
 * Extended menu route with additional fields specific to the own-level menu
 */
interface ExtendedMenuRoute extends MenuRoute {
  param?: string;
  title?: string;
  views?: ExtendedMenuRoute[];
}

/**
 * Return type of useOwnLevelMenu hook
 */
interface OwnLevelMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: ExtendedMenuRoute[];
}

/**
 * Hook for the own-level menu: artifacts the selected organisation owns itself.
 *
 * "Own-level" in STIC-15615 terms — things that belong to System (the
 * organisation register, partners, price lists, invoicing, Salesforce,
 * Impartner, SIM/Sigfox accounts, internal tooling) or to a partner (its own
 * tags and organisation filters). None of it is per-customer-org data.
 *
 * Everything that IS per-org data — trackers, geozones, users, alerts,
 * customization, developers — lives in useCrossLevelMenu, which serves the
 * cross-org page when no `?org=` is set and the org-scoped page when one is.
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @param orgId - Optional organization ID to load specific organization context
 * @returns Menu data including routes, available paths, and loading state
 */
const useOwnLevelMenu = (
  loginInfo: LoginInfo,
  orgId?: number,
  level: MenuLevel = MenuLevelConst.CROSS
): OwnLevelMenuData => {
  const { custumInfo, organisation } = loginInfo;
  const [orgSelected, setOrgSelected] = useState<ExtendedOrganisationInfo | null>(null);
  // The org id that `orgSelected` has finished resolving for (set even when a
  // fetch fails), so the "menu caught up to the URL org" check below can't get
  // stuck loading if organizationClient.findOne rejects.
  const [resolvedOrgId, setResolvedOrgId] = useState<number | null>(null);
  /** Which level `data` below was actually computed for. See `levelNotReady`. */
  const [resolvedAtOwnLevel, setResolvedAtOwnLevel] = useState<boolean | null>(null);

  const isSystem = organisation?.organisationType === "SYSTEM";
  // Either partner kind — channel or multi-org.
  const isPartnerLogin = organisation?.organisationType === "PARTNER";
  // The enhanced org type the visibility rules are written against. Every entry
  // below states, in its own `view:` / `disabled:`, which of these four may see
  // it at own level; see OrgTypeConst in menuTypes for the full contract and the
  // spreadsheet it implements.
  const loginOrgType = getEnhancedOrgType(loginInfo);
  // NORMAL is the one type the spreadsheet does not describe. Entries the sheet
  // hides for all three of SYSTEM/PARTNER/MULTI_ORG say `isNormalLogin` rather
  // than `false`, so a single-org customer admin keeps today's behaviour.
  const isNormalLogin = loginOrgType === OrgTypeConst.NORMAL;
  // "Own level" = the login is looking at its own organisation. For SYSTEM that
  // is the Sensolus org, for a partner its own partner org — both reached via
  // the first tab of the header org selector.
  //
  // Since STIC-15615 that is exactly "the URL carries no ?org=", which `level`
  // already answers. Deliberately URL-derived rather than taken from the fetched
  // `orgSelected`: that lands a beat later, and in the gap the own-level items
  // would read as unavailable — enough to bounce any login whose configured
  // landing page is an own-level page (e.g. /organization) straight back out.
  const atOwnLevel = level === MenuLevelConst.OWN;
  // Whether this login has a Home menu at all (STIC-15365) — decides who owns
  // the agent detail page below.
  const homeMenuReachable = isHomeMenuReachable(loginInfo);
  // STIC-16186 R1. The Dashboards group belongs to whichever menu the login
  // actually has: Home when there is one, own level when there is not. Keyed on
  // `homeMenuReachable` rather than on the feature key it reads, so it stays the
  // one predicate that answers "does this login have a Home menu" — a NORMAL
  // login has one and must not see the group twice.
  const showDashboardGroup =
    atOwnLevel &&
    !homeMenuReachable &&
    (hasFeature(FeatureKey.utilization_suez) ||
      (hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_HOME_DASHBOARD)));
  // STIC-16186 R2, the same rule for agents. Deliberately not testing the
  // feature key or the permission here — useAgentMenuItems does that, together
  // with R8 (at least one enrolled agent), because only the fetch knows the count.
  const showAgentGroup = atOwnLevel && !homeMenuReachable;

  const initialRoutes = useMemo<ExtendedMenuRoute[]>(() => {

    return [
      // Sensolus-internal landing dashboards. Own-level by definition: they
      // aggregate System's own view of the business, not one org's data.
      // Explicitly isSystem-gated — partners reach this menu too now, and the
      // rest of the block is only incidentally safe via SYSTEM_ADMIN_VIEW.
      {
        collapse: false,
        view: isSystem,
        disabled: false,
        name: translate(LanguageKey.partner_partners_key),
        state: "partnersDashboard",
        to: "/partners_dashboard",
        layout: "",
        icon: <span className="partner-admin-menu-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      {
        collapse: false,
        view:
          isSystem &&
          hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
        disabled: false,
        name: translate(LanguageKey.system_key),
        state: "systemDashboard",
        to: "/system_dashboard",
        layout: "",
        icon: <span className="system-menu-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      {
        collapse: false,
        view:
          isSystem &&
          hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
        disabled: false,
        name: translate(LanguageKey.finance_landing_key),
        state: "financeDashboard",
        to: "/finance_dashboard",
        layout: "",
        icon: <span className="invoicing-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      {
        collapse: false,
        view:
          isSystem &&
          hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
        disabled: false,
        name: translate(LanguageKey.support_landing_key),
        state: "supportDashboard",
        to: "/support_dashboard",
        layout: "",
        icon: <span className="support-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      {
        collapse: false,
        view:
          isSystem &&
          hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
        disabled: false,
        name: translate(LanguageKey.customer_success_landing_key),
        state: "customerSuccessDashboard",
        to: "/customer_success_dashboard",
        layout: "",
        icon: <span className="help-menu-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      {
        collapse: false,
        view:
          isSystem &&
          hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
        disabled: false,
        name: translate(LanguageKey.sales_landing_key),
        state: "salesDashboard",
        to: "/sales_dashboard",
        layout: "",
        icon: <span className="sales-menu-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      {
        collapse: false,
        view:
          isSystem &&
          hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
        disabled: false,
        name: translate(LanguageKey.presales_landing_key),
        state: "presalesDashboard",
        to: "/presales_dashboard",
        layout: "",
        icon: <span className="presales-menu-icon menu-icon"></span>,
        views: [],
        topOverview: true,
      },
      // Favourited dashboards and "All dashboards" (STIC-16186 R1). The
      // run-time counterpart of Insights → Dashboards (/bi_dashboards), which
      // manages them: this group opens them. It used to sit on the cross level,
      // which read as "these follow the org slicer" — they never did, they are
      // the login's own favourites. So it lives at own level now, and only for
      // logins with no Home menu to carry it: SYSTEM always, a partner without
      // legacy_partner_home_menu. First of the collapse sections, above
      // Presales — the landing pills above it are a band of their own.
      //
      // Unlike its siblings it needs an explicit `view`: its children are
      // fetched by useDashboardMenuItems below, and `pruneHidden` keeps a
      // section whose `views` is still empty, so there is nothing to prune it
      // by in the render before they land.
      {
        view: showDashboardGroup,
        collapse: true,
        name: translate(LanguageKey.UserPermission_MENU_HOME_DASHBOARD_description_key),
        state: DASHBOARD_GROUP_STATE,
        views: [],
      },
      // Favourited agents and "All agents" (STIC-16186 R2), the run-time
      // counterpart of Insights -> Agents (/enrolled_agents) just as the group
      // above is of Insights -> Dashboards. Until this existed, /all_agents was
      // registered by the Home menu alone, so a login without one — SYSTEM, a
      // partner without legacy_partner_home_menu — could not reach it at all.
      //
      // `view` starts false and is decided by useAgentMenuItems below: R8 hides
      // the group entirely unless the organisation has at least one enrolled
      // agent, which only the fetch can answer.
      {
        view: false,
        collapse: true,
        name: translate(LanguageKey.agents_section_title_key),
        state: AGENT_GROUP_STATE,
        views: [],
      },
      // Presales (STIC-15790): what happens before an organisation buys -
      // quotes, demo environments and the battery calculator.
      {
        collapse: true,
        name: translate(LanguageKey.presales_key),
        state: "openPresales",
        views: [
          {
            // The partner-facing quotation tool. /quotes_storefront is internal
            // Sensolus tooling and its route condition (INTERNAL_QUOTES_ACCESS)
            // rejects PARTNER logins outright, so own level must point at the
            // _partner variant - which carries the commercial + EU rule that
            // PARTNER_QUOTES_ACCESS enforces. Pointing here at the internal path
            // is what made this item a dead link for partners (STIC-15790).
            // Row 15 + STIC-16004: a partner's own quoting tool, so `isPartnerLogin`
            // rather than `!isSystem`, and open only to a commercial channel partner
            // on the EU price list (marks 1, 2, 3 of the Partner Variant Access
            // Matrix). Every multi-org-like variant and every non-commercial channel
            // partner is out; a NORMAL login never gets here. The three-part rule
            // lives in utils/directBusiness.ts, shared with the route guard.
            view:
              isPartnerLogin &&
              atOwnLevel &&
              hasFeature(FeatureKey.quote_manage) &&
              hasPermission(UserPermission.MENU_ADMIN_QUOTES) &&
              loginIsCommercialEuChannelPartner(loginInfo),
            disabled: false,
            isForceAvailableMenu: true,
            layout: "",
            name: translate(LanguageKey.quotes_key),
            to: "/quotes_storefront_partner",
            subPages: ["/quotes_partner", "/quote_detail_partner"],
          },
          {
            view:
              isSystem &&
              atOwnLevel &&
              hasFeature(FeatureKey.quote_manage) &&
              hasPermission(UserPermission.MENU_ADMIN_QUOTES),
            disabled: false,
            isForceAvailableMenu: true,
            layout: "",
            name: translate(LanguageKey.quotes_sensolus_key),
            to: "/quotes_storefront",
            subPages: ["/quotes", "/quote_detail"],
          },
          {
            view:
              atOwnLevel &&
              (isSystem || loginIsCommercialChannelPartner(loginInfo)) &&
              hasFeature(FeatureKey.demo_environment) &&
              hasPermission(UserPermission.MENU_ADMIN_DEMO),
            disabled: false,
            to: "/org_demo",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DEMO_description_key),
            subPages: ["/org_demo_manage"],
          },
          {
            // Own level only — the matrix ("/battery_calculator") hides it at cross
            // level. The login-type test is only here to keep NORMAL logins out,
            // which the matrix deliberately does not cover.
            view:
              atOwnLevel &&
              (isSystem || loginIsCommercialChannelPartner(loginInfo)) &&
              hasPermission(UserPermission.MENU_ADMIN_BATTERY_CALCULATOR),
            disabled: false,
            to: "/battery_calculator",
            layout: "",
            name: translate(LanguageKey.battery_calc_battery_calculator_key),
          },
        ],
      },
      // Sensolus support (STIC-15790): the running business between the
      // organisation and Sensolus - orders, support, returns, billing - for a
      // partner and for a direct customer alike.
      {
        collapse: true,
        name: translate(LanguageKey.sensolus_support_group_key),
        state: "openSensolusSupport",
        views: [
          {
          view:
            // Purchase orders are a partner/customer-to-Sensolus matter; System has
            // no counterpart of its own, so the entry is hidden in System scope.
            !isSystem &&
            atOwnLevel &&
            loginHasDirectBusiness(loginInfo) &&
            (hasFeature(FeatureKey.zendesk_ticket) &&
            hasPermission(UserPermission.MENU_ADMIN_ZENDESK_ORDER)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ZENDESK_ORDER_description_key),
            to: "/order_tickets",
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            loginHasDirectBusiness(loginInfo) &&
            (hasFeature(FeatureKey.zendesk_ticket) &&
            hasPermission(UserPermission.MENU_ADMIN_ZENDESK_SUPPORT_TICKET)),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_ZENDESK_SUPPORT_TICKET_description_key
          ),
            to: "/support_tickets",
          },
          {
          view:
            // The organisation's side of the business with Sensolus, so System has no
            // entry of its own. Unlike its neighbours it carries no direct-business
            // rule: everyone returns hardware to Sensolus, including an indirect
            // customer, so the wiki keeps this one open to every organisation.
            //
            // But only while there is an open return (STIC-16004 R5). Sensolus
            // raises every return, so for most organisations the page is empty, and
            // as the first item of an indirect customer's menu it advertised faulty
            // hardware. The count comes with the login info, so the menu makes no
            // request. No count means nothing to show: only a new organisation has
            // none, and it has no returns yet.
            !isSystem &&
            atOwnLevel &&
            hasPermission(UserPermission.MENU_ADMIN_RMA_DECLARATION) &&
            (organisation?.numberOfOpenRMA ?? 0) > 0,
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key),
            to: "/rma_declaration_view",
            subPages: [
              "/rma_declaration_view_details",
            ],
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            loginHasDirectBusiness(loginInfo) &&
            hasPermission(UserPermission.MENU_ADMIN_BILLING),
          disabled: false,
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_BILLING_description_key),
            to: "/invoicing",
            subPages: [
              "/invoice_detail",
              "/quotation_detail",
            ],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.organizations_key),
        state: "openSystemOrganizations",
        views: [
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_PARTNERS),
            disabled: false,
            to: "/partners",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNERS_description_key),
            subPages: ["/partners_update", "/partners_create"],
          },
          // STIC-15615 R2 / STIC-15365: the organisation register is an own-level
          // artifact for whoever owns the organisations — System owns all of
          // them, a partner owns the ones it manages. Partners reach it from
          // their own "Partner" tab, gated on partner_org_manage exactly as the
          // retired usePartnerMenu did.
          ...(isSystem || isPartnerLogin
            ? [
                {
                  view:
                    atOwnLevel &&
                    hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_LIST) &&
                    (isSystem ||
                      (hasFeature(FeatureKey.partner_org_manage) &&
                        !loginInfo.lastOrganisationFilters)),
                  disabled: false,
                  to: "/organization",
                  layout: "",
                  name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_LIST_description_key),
                  // "Add new organization" opens /organization_update?type=add with
                  // no ?org= — it is this level's own action, so it must be
                  // available here. Without this subPage the path resolved to the
                  // own-level "Organization details" entry, which is hidden for
                  // System, and the sidebar bounced the user to /no_access.
                  //
                  // Opening an EXISTING organisation carries ?org=<id> and means
                  // browsing that org, which is cross-level (useCrossLevelMenu
                  // owns it). SidebarBody's drill-in rule only holds the own
                  // level for junk/absent org values, so the real id wins there.
                  subPages: ["/organization_update"],
                },
              ]
            : []),
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.trackers_and_sensors_key),
        state: "openTrackersSensors",
        views: [
          // Order follows the "Admin menu" sheet: the organisation's own tracker
          // data first, then the Sensolus-internal product administration.
          {
            // Own level only, and gated on a feature key only a few organisations
            // hold — uploads are always made against the organisation you are
            // logged into. Hidden in useCrossLevelMenu.
            view: atOwnLevel && hasFeature(FeatureKey.factory_upload),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.sidebar_factory_upload_key),
            to: "/factory_upload",
            subPages: ["/factory_upload_update", "/factory_order_create"],
          },
          {
            view: atOwnLevel && hasPermission(UserPermission.MENU_ADMIN_TRACKERS),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKERS_description_key),
            to: "/trackers",
            subPages: [
              "/trackers_admin_update",
              "/import_tracker_settings",
              "/import_tracker_settings_admin",
            ],
          },
          {
            view: atOwnLevel && hasPermission(UserPermission.MENU_ADMIN_BLE_SENSOR),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key),
            to: "/ble_sensor_list",
            subPages: ["/ble_sensor_update", "/ble_sensor_import"],
          },
          {
            // Visible at own level for every org type; the NORMAL-org restriction
            // only applies when browsing one from cross level (see useCrossLevelMenu).
            view: atOwnLevel && hasPermission(UserPermission.MENU_ADMIN_USAGE_PROFILE),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key),
            to: "/device_usage_profile",
            // Own level only — see OWN_LEVEL_DETAIL_ROUTES. The cross menu
            // declares the `_admin` twin, which is what makes this path
            // own-exclusive: SidebarBody holds the own menu for it whatever
            // `?org=` says, and OrgContext resolves the LEVEL from the same
            // list. That is what lets a record shared down from above open here
            // with its OWNER in `?org=`.
            subPages: [
              OWN_LEVEL_DETAIL_ROUTES["/device_usage_profile"].own,
              "/device_usage_profile_compare",
            ],
          },
          {
            // Row 29: own level for every customer-facing login — a partner has
            // diagnostics of its own, not only its customers'.
            view:
              (isNormalLogin || isPartnerLogin) &&
              atOwnLevel &&
              ((hasFeature(FeatureKey.diagnostic_tab) ||
                hasFeature(FeatureKey.diagnostic_organisation)) &&
                hasPermission(UserPermission.MENU_ADMIN_DIAGNOSTIC)),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DIAGNOSTIC_description_key),
            to: "/diagnostic_organisation",
          },

          // ---- Sensolus-internal product administration ----
          {
            view:
              atOwnLevel &&
              (hasFeature(FeatureKey.bulk_operations) &&
                hasPermission(UserPermission.MENU_ADMIN_BULK_OPERATION)),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_BULK_OPERATION_description_key),
            to: "/bulk_manage",
            subPages: [
              "/bulk_operations",
              "/bulk_operations_admin",
              "/bulk_results_detail",
              "/bulk_results_detail_system",
            ],
          },
          {
            view: atOwnLevel && hasPermission(UserPermission.MENU_ADMIN_EOL_TRACKERS),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_EOL_TRACKERS_description_key),
            to: "/end_of_life",
          },
        ],
      },
      // Reports group (STIC-15790): visible for every login type at both
      // levels — only feature/permission gate it. Report list and Report
      // schedules deliberately duplicate the home-menu pages on admin-side
      // routes (/reports_admin, /report_schedule_admin): the home menu keeps
      // /reports and /report_schedule, and sharing those paths made HOME win
      // the menu resolution over the admin menus.
      {
        collapse: true,
        name: translate(LanguageKey.reports_key),
        state: "openReports_own",
        views: [
          {
            view:
              atOwnLevel &&
              (hasFeature(FeatureKey.report_generator) &&
                hasPermission(UserPermission.MENU_HOME_REPORT_LIST)),
            disabled: false,
            layout: "",
            name: translate(
              LanguageKey.UserPermission_MENU_HOME_REPORT_LIST_description_key
            ),
            to: "/reports_admin",
            subPages: ["/create_report"],
          },
          {
            view:
              atOwnLevel &&
              (hasFeature(FeatureKey.report_generator) &&
                hasPermission(UserPermission.MENU_HOME_REPORT_SCHEDULES)),
            disabled: false,
            layout: "",
            name: translate(
              LanguageKey.UserPermission_MENU_HOME_REPORT_SCHEDULES_description_key
            ),
            to: "/report_schedule_admin",
          },
          {
            view:
              atOwnLevel &&
              (hasFeature(FeatureKey.report_generator) &&
                hasPermission(UserPermission.MENU_ADMIN_REPORT_TEMPLATE)),
            disabled: false,
            layout: "",
            name: translate(
              LanguageKey.UserPermission_MENU_ADMIN_REPORT_TEMPLATE_description_key
            ),
            to: "/report_template_library",
            subPages: ["/report_template_library_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.location_key),
        state: "openLocation",
        views: [
          // Same order as the cross-level menu (STIC-15790); the Sensolus-internal
          // entries the cross level does not have follow at the end.
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (canSeeAtOwnLevel(FeatureKey.geozone_manager, FeatureKey.legacy_partner_level_geozones) &&
            hasPermission(UserPermission.MENU_ADMIN_GEOZONES)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONES_description_key),
            to: "/areas",
            // Own level only — see OWN_LEVEL_DETAIL_ROUTES. The cross menu
            // declares the `_admin` twin, which is what makes this path
            // own-exclusive: SidebarBody holds the own menu for it whatever
            // `?org=` says, and OrgContext resolves the LEVEL from the same
            // list. That is what lets a record shared down from above open here
            // with its OWNER in `?org=`.
            subPages: [
              OWN_LEVEL_DETAIL_ROUTES["/areas"].own,
              "/import_export_geozones",
            ],
          },
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.loc_geolayer) && hasPermission(UserPermission.MENU_ADMIN_GEOLAYER)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOLAYER_description_key),
            to: "/geolayers",
            subPages: ["/geolayer_update"],
          },
          {
          // STIC-16142: a site map can only be owned by a normal organisation, so a
          // partner has nothing at its own level - it works on its customers' site
          // maps through the explorer. Hidden for identity, not greyed for context.
          view:
            isNormalLogin &&
            atOwnLevel &&
            (hasFeature(FeatureKey.loc_site_map) &&
            hasPermission(UserPermission.MENU_ADMIN_SITE_MAP)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_SITE_MAP_description_key),
            to: "/site_maps",
            subPages: ["/site_map_update"],
          },
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.loc_beacon) &&
            hasPermission(UserPermission.MENU_ADMIN_INFRASTRUCTURE)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key),
            to: "/infrastructures",
            subPages: [
              "/infrastructures_update",
              "/import_infrastructure_settings",
              "/import_infrastructure_settings_admin",
            ],
          },
          {
          // Row 45 hides it at every own column, same as Location settings.
          view:
            isNormalLogin &&
            atOwnLevel &&
            (hasFeature(FeatureKey.private_wifi_resolution) &&
            hasPermission(UserPermission.MENU_ADMIN_WIFI)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_WIFI_description_key),
            to: "/wifi",
            subPages: [
              "/wifi/blocked",
              "/wifi/unresolved",
            ],
          },
          {
            // A customer feature, like Wi-Fi localization above: organisations
            // whose trackers resolve against their own private cell towers manage
            // those towers here. The feature gate was dropped in STIC-13773 and is
            // restored here (STIC-15790); the backend requires it anyway.
            view:
              atOwnLevel &&
              // Row 46: every login type has it at own level. The feature key
              // belongs to a customer organisation and System has none of its
              // own, hence the either/or.
              (isSystem || hasFeature(FeatureKey.only_private_cellular_resolution)) &&
              hasPermission(UserPermission.MENU_ADMIN_PRIVATE_CELLULAR),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.cellular_tower_adjustments_key),
            to: "/private_cellular",
          },
          {
          // STIC-15615 R11: location settings configure ONE normal org and never
          // inherit downwards. Hidden for System; a partner's own level keeps the
          // item visible but greyed, and the page explains why.
          // Configures ONE customer org, so row 50 hides it at every own column —
          // a partner reaches a customer's settings through the cross level.
          view:
            isNormalLogin &&
            atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_LOCATION_SETTINGS) ||
              hasPermission(UserPermission.GEOZONE_BEHAVIOR_VIEW) ||
              hasPermission(UserPermission.JOURNEY_ALGO_SETTINGS_VIEW)),
          disabled: false,
          param: hasPermission(UserPermission.BLE_TAG_TRACKING_SETTINGS_MANAGE)
            ? "type=edit"
            : "type=view",
          layout: "",
          name: translate(LanguageKey.location_settings_key),
            to: "/location_settings",
          },
          // ---- Sensolus-internal location tooling ----
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.types_and_tags_key),
        state: "openTags",
        views: [
          {
            // Asset types are a customer-org concept. Row 56 hides them at every
            // own column but NORMAL's — a partner reaches its customers' through
            // the cross level.
            view:
              isNormalLogin &&
              atOwnLevel &&
              hasPermission(UserPermission.MENU_ADMIN_ASSET_TYPES),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ASSET_TYPES_description_key),
            to: "/asset_types",
          },
          {
            // Own level of a partner login. The matrix ("/partner_tag") hides it
            // for a System login and at cross level.
            view:
              !isSystem &&
              atOwnLevel &&
              isPartnerLogin &&
              hasPermission(UserPermission.MENU_ADMIN_PARTNER_TAGS),
            disabled: false,
            isForceAvailableMenu: true,
            to: "/partner_tag",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNER_TAGS_description_key),
            subPages: ["/partner_tag_manage"],
          },
          {
            view: !!(
              orgSelected &&
              "SYSTEM" === orgSelected.organisationType &&
              hasPermission(UserPermission.MENU_ADMIN_SYSTEM_TAGS)
            ),
            disabled: false,
            isForceAvailableMenu: true,
            to: "/system_tag",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SYSTEM_TAGS_description_key),
            subPages: ["/system_tag_manage"],
          },
        
          // ---- org data at own level (no ?org= in the URL) ----
          {
          view:
            isNormalLogin &&
            atOwnLevel && hasPermission(UserPermission.MENU_ADMIN_ASSET_TAGS),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ASSET_TAGS_description_key),
            to: "/asset_tags_manage",
            subPages: [
              "/update_device_tag",
            ],
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (canSeeAtOwnLevel(FeatureKey.geozone_manager, FeatureKey.legacy_partner_level_geozones) &&
            hasPermission(UserPermission.MENU_ADMIN_GEOZONE_TAGS)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONE_TAGS_description_key),
            to: "/geozone_tags_manage",
            subPages: [
              "/update_geozone_tag",
            ],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.access_management_key),
        state: "openAccess",
        views: [
        
          // ---- org data at own level (no ?org= in the URL) ----
          {
          view: atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_USERS)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_USERS_description_key),
            to: "/users",
            subPages: [
              "/users_update",
              "/users_admin_update",
            ],
          },
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.custom_user_roles) &&
            hasPermission(UserPermission.MENU_ADMIN_USER_ROLE)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_USER_ROLE_description_key),
            to: "/role_manager",
            // Own level only. Both menus used to declare both routes, which made
            // neither own-exclusive -- and SidebarBody can only hold the own menu
            // for a path the CROSS menu does not also claim
            // (`ownOnly = ownRoute && !crossRoute`, SidebarBody.tsx:253). So a
            // role opened at own level flipped the sidebar to the cross menu the
            // moment its owner reached the url. One route per menu fixes that at
            // the root: the path itself carries the level. That has to hold in
            // the LEVEL too, not just in SidebarBody's probe -- `view` above
            // gates on `atOwnLevel`, so a foreign owner in `?org=` would have
            // unregistered this very path and left the sidebar empty. Hence
            // OrgContext resolves `level` from this same list (OWN_LEVEL_PATHS),
            // and hence this reads it rather than repeating the literal.
            subPages: [OWN_LEVEL_DETAIL_ROUTES["/role_manager"].own],
          },
          {
            view:
              !isSystem &&
              atOwnLevel &&
              isPartnerLogin &&
              hasFeature(FeatureKey.organisation_visibility_filters) &&
              hasPermission(UserPermission.MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER),
            disabled: false,
            isForceAvailableMenu: true,
            to: "/manage_organisation_filters",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER_description_key),
            subPages: [
              "/manage_organisation_filters_update",
              // Was a subPage of usePartnerMenu's _partner variant (STIC-15365);
              // re-homed here so the partner edit page stays reachable.
              "/manage_partner_organisation_filters_update",
            ],
          },
          {
          view:
            isNormalLogin &&
            atOwnLevel &&
            (hasFeature(FeatureKey.visibility_filters) &&
            hasPermission(UserPermission.MENU_ADMIN_VISIBILITY_FILTER)),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_VISIBILITY_FILTER_description_key
          ),
            to: "/manage_visibility_filters",
            subPages: [
              "/manage_visibility_filters_update",
            ],
          },
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.audit_log) && hasPermission(UserPermission.MENU_ADMIN_AUDIT_LOG)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_AUDIT_LOG_description_key),
            to: "/audit_log",
            subPages: [
              "/audit_log_view",
              "/audit_log_aggregated_view",
            ],
          },
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.int_sso) &&
            hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_SSO) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_SSO_description_key
          ),
            to: "/single_sign_on",
          },
        ],
      },
      // The CSM team's own working views (STIC-15790). Sensolus-internal:
      // customer-success notes and the internal asset classification.
    {
      collapse: true,
      name: translate(LanguageKey.insights_key),
      state: "openInsights_own",
      views: [
          {
            // BI dashboard management for the organisation you are logged in as
            // (design decision, STIC-15790): every login type can own dashboards -
            // System, partners and normal organisations alike - so all manage them
            // here; the cross-level twin under Insights manages a browsed org's.
            // No `category: "dashboard"` on purpose - NavList would append ?org=
            // to the link and leave the own level.
            // Row 62 hides it at System's own level — the Sensolus org has no
            // dashboards of its own.
            view:
              atOwnLevel &&
              !isSystem &&
              hasFeature(FeatureKey.dashboard) &&
              hasPermission(UserPermission.MENU_ADMIN_DASHBOARD),
            disabled: false,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_DASHBOARD_description_key),
            to: "/bi_dashboards",
            subPages: ["/bi_dashboard_manage"],
          },
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.agents) && hasPermission(UserPermission.MENU_ADMIN_AGENT)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.agents_section_title_key),
          // Every page reached from the list, so the entry stays highlighted once
          // you drill into one. Named by prefix — subPage matching is
          // prefix-based, and each route carries an agent name or id.
          //
          // Deliberately not "/enrolled_agents" itself, which would cover all
          // three: SidebarBody skips defaulting ?org= for a path listed here
          // verbatim, so listing the list's own path would break deep links that
          // arrive without an org. That is why each detail page has a fixed
          // segment of its own rather than hanging off the list path directly
          // (STIC-16035).
            to: "/enrolled_agents",
            subPages: [
              "/enrolled_agents/registered",
              "/enrolled_agents/open",
              "/enrolled_agents/schedules",
              // The agent detail page (/agents/<name>) the list links to. It is a
              // Home path, and rule 1 of the ladder would hand it to this menu at
              // own level before the Home menu gets a look — so it is registered
              // here only for logins that have no Home menu to claim it, which is
              // what keeps it from being menu-less for them (STIC-15365).
              //
              // Widened from "a partner without the key" to every such login when
              // the own-level Agents group arrived (STIC-16186 R2): that group
              // registers only the *favourited* agents, so opening any other one
              // from "All agents" had nothing to resolve by, and SYSTEM — which
              // falls back to the cross menu — watched its sidebar flip. Prefix,
              // like above.
              ...(!homeMenuReachable ? ["/agents"] : []),
            ],
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasFeature(FeatureKey.alert_ruleconf) &&
            hasPermission(UserPermission.MENU_ADMIN_ALERT_RULE)),
          disabled: false,
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ALERT_RULE_description_key),
            to: "/manage_alerts",
            subPages: [
              "/manage_alerts_update",
            ],
          },
          {
          // Utilization KPIs are owned by one organisation. A customer needs
          // utilization_basic; a partner's own-level KPIs are a legacy grant of
          // utilization_multi_org, a legacy per-org grant (STIC-15987) - other partners create
          // and manage KPIs inside a customer org from the cross level, on permission.
          view:
            atOwnLevel &&
            canSeeAtOwnLevel(FeatureKey.utilization_basic, FeatureKey.utilization_multi_org) &&
            hasPermission(UserPermission.MENU_ADMIN_DEVICE_UTILIZATION_SCHEME),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_DEVICE_UTILIZATION_SCHEME_description_key
          ),
            to: "/utilization_scheme",
            subPages: [
              "/utilization_scheme_update",
            ],
          },
          {
          // Row 65: a flow is configured on ONE organisation. A customer needs
          // flow_definition; flows owned by the partner account itself are a legacy
          // grant of flow_multi_org (STIC-15987) - every other partner
          // reaches flows inside a customer org from the cross level, on permission.
          view:
            atOwnLevel &&
            canSeeAtOwnLevel(FeatureKey.flow_definition, FeatureKey.flow_multi_org) &&
            hasPermission(UserPermission.MENU_ADMIN_FLOW),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_FLOW_description_key),
            to: "/flow_definition",
            subPages: [
              "/flow_settings",
            ],
          },
          {
          // Row 66: same as Flows — one customer organisation's configuration, so
          // a partner reaches it from the cross level and sees nothing here.
          view:
            isNormalLogin &&
            atOwnLevel &&
            (hasFeature(FeatureKey.process_manager) &&
            hasPermission(UserPermission.MENU_ADMIN_PROCESS_DEFINITION)),
          disabled: false,
          isForceAvailableMenu: true,
          layout: "",
          title: translate(
            LanguageKey.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key
          ),
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key
          ),
            to: "/process_definition",
            subPages: [
              "/process_definition_update",
            ],
          },
          {
          // Row 68: a maintenance program can only be OWNED by a customer organisation
          // (MaintenanceProgramServiceImpl refuses any other type), and this page lists
          // what the organisation in scope owns. So at own level it is for a customer
          // login only: a partner's own level could never be anything but empty -
          // confirmed on production, zero programs are owned by a partner org. The
          // partner's aggregated view over its customers is the HOME menu, which is
          // what maintenance_multi_org buys; inside a customer org it works from the
          // cross level, on permission (STIC-15987).
          view:
            atOwnLevel &&
            isNormalLogin &&
            hasFeature(FeatureKey.maintenance_basic) &&
            hasPermission(UserPermission.MENU_ADMIN_MAINTENANCE),
          disabled: false,
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_MAINTENANCE_description_key),
            to: "/maintenance_programs",
            subPages: [
              "/maintenance_programs_manage",
              "/maintenance_programs_bulk_start",
            ],
          },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.organization_settings_key),
      state: "openOrgSetting_own",
      views: [
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_DETAILS) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          param: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) ? "type=edit" : "type=view",
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_DETAILS_description_key
          ),
            to: "/organization_update",
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_PLATFORM_PLAN) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          param: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) ? "type=edit" : "type=view",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_PLATFORM_PLAN_description_key),
            to: "/product_plan",
          },
          {
          // Row 91 keeps this on System's cross level only.
          view:
            isNormalLogin &&
            atOwnLevel && hasPermission(UserPermission.MENU_ADMIN_PARTNER_DETAIL),
          disabled: false,
          isForceAvailableMenu: true,
          param: "type=view&id=&tabActive=general_tab",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNER_DETAIL_description_key),
            to: "/partners_details",
          },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.customization_key),
      state: "opencustomization_own",
      views: [
          {
          view: atOwnLevel &&
            (hasFeature(FeatureKey.image_library) &&
            hasPermission(UserPermission.MENU_ADMIN_IMAGE_LIBRARY)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key),
            to: "/image_library",
            subPages: [
              "/image_library_manage",
            ],
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasFeature(FeatureKey.business_inject) &&
            hasPermission(UserPermission.MENU_ADMIN_BUSINESS_CHANNEL) &&
            // MENU_ADMIN_BUSINESS_CHANNEL declares BUSINESS_CHANNEL_VIEW as a
            // dependency, but the role editor only cascades dependencies when a
            // permission is checked, never when one is unchecked. Roles can therefore
            // keep the menu permission while the definition permissions are revoked -
            // check it explicitly here (STIC-15894).
            hasPermission(UserPermission.BUSINESS_CHANNEL_VIEW) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_BUSINESS_CHANNEL_description_key
          ),
            to: "/third_party_data",
            subPages: [
              "/channel_update",
            ],
          },
          {
          // Only customer organisations own automation rules; the visibility
          // matrix ("/automation_rule") greys the item for a partner org and "All".
          // Row 67 hides it at every own column: rules belong to a customer
          // organisation, which a partner reaches through the cross level.
          view:
            isNormalLogin &&
            atOwnLevel &&
            (hasFeature(FeatureKey.automation_manager) &&
            hasPermission(UserPermission.MENU_ADMIN_AUTOMATION)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.automation_rules_key),
            to: "/automation_rule",
            subPages: [
              "/automation_rule_create",
              "/automation_rule_update",
            ],
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasFeature(FeatureKey.custom_interface) &&
            hasPermission(UserPermission.MENU_ADMIN_CUSTOM_INTERFACE) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_CUSTOM_INTERFACE_description_key),
            to: "/custom_branding",
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_SUPPORT)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_SUPPORT_description_key),
            to: "/support",
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasFeature(FeatureKey.landing_page) &&
            hasPermission(UserPermission.MENU_ADMIN_LANDING_PAGE) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_LANDING_PAGE_description_key),
            to: "/default_landing_page",
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasFeature(FeatureKey.custom_i18n_message) &&
            hasPermission(UserPermission.MENU_ADMIN_CUSTOM_I18N_MESSAGE)),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_CUSTOM_I18N_MESSAGE_description_key
          ),
            to: "/custom_i18n_messages",
            subPages: [
              "/custom_i18n_messages_manage",
            ],
          },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.developers_key),
      state: "openDevelopers_own",
      views: [
          {
          view: atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_API_DOCS)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_DOCS_description_key),
            to: "/api-docs",
          },
          {
          view: atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_API_ACCESS)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_ACCESS_description_key),
            to: "/api-access",
            subPages: [
              "/api-access-update",
            ],
          },
          {
          view:
            !isSystem &&
            atOwnLevel &&
            (hasFeature(FeatureKey.int_rest_mqtt_push) &&
            hasPermission(UserPermission.MENU_ADMIN_WEBHOOKS) &&
            (organisation?.organisationType !== "PARTNER" ||
              hasFeature(FeatureKey.partner_org_manage))),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_WEBHOOKS_description_key),
            to: "/webhooks",
            subPages: [
              "/callbacks_update",
            ],
          },
      ],
    },
      {
        collapse: true,
        name: translate(LanguageKey.operations_team_key),
        state: "openSupport",
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INVOICING_OPERATIONS),
            disabled: false,
            to: "/invoicing_operations",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INVOICING_OPERATIONS_description_key),
            subPages: ["/invoicing_operations_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INVOICES),
            disabled: false,
            to: "/invoices",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INVOICES_description_key),
            subPages: ["/invoices_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_RENEWAL_FORECASTS),
            disabled: false,
            to: "/quotation",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_RENEWAL_FORECASTS_description_key),
            // /quotation_detail is registered by cross-level's /invoicing entry,
            // which wins the resolution order — listing it here only made the
            // path ambiguous.
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_STOCK),
            disabled: false,
            to: "/stock",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_STOCK_description_key),
            subPages: ["/stock_update"],
          },
          {
            // STIC-16159: Sensolus' side of device returns - the list where a
            // return is declared, edited and given its internal notes. The
            // customer/partner side is "Device returns (RMA)" under Sensolus
            // support (/rma_declaration_view). Own-level System only, like
            // Zendesk ticket next to Support tickets; it used to sit at Explorer
            // "All", which is a browse position and not where Sensolus' own work
            // belongs. Adding a return needs RMA_DECLARATION_MANAGE on top.
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_RMA_DECLARATION),
            disabled: false,
            to: "/rma_declaration",
            layout: "",
            name: translate(LanguageKey.rma_declarations_key),
            subPages: ["/rma_declaration_manage"],
          },
          {
            // SYSTEM only. The zendesk_ticket feature key used to gate this too,
            // copied over from the customer-facing "Support tickets" page, but
            // this list is internal-only and the backend never checked it.
            view:
              isSystem &&
              (hasPermission(UserPermission.SYSTEM_ADMIN_MANAGE) ||
                hasPermission(UserPermission.SYSTEM_ADMIN_VIEW)),
            disabled: false,
            to: "/zendesk_tickets",
            layout: "",
            name: translate(LanguageKey.zendesk_ticket_key),
          },
        
          // ---- org data at own level (no ?org= in the URL) ----
          {
            view:
              isSystem &&
              // STIC-15615 R10: own-level "System" ONLY — no cross-level and
              // no org-scoped variant anymore
              orgSelected?.organisationType === "SYSTEM" &&
              hasFeature(FeatureKey.stock_movement) &&
              hasPermission(UserPermission.MENU_STOCK_MOVEMENT),
            disabled: false,
            to: `/stock_movement`,
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_STOCK_MOVEMENT_description_key),
            subPages: ["/stock_movement_view"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRICE_BOOKS),
            disabled: false,
            to: "/pricebook",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRICE_BOOKS_description_key),
            subPages: ["/pricebook_update", "/pricebook_increase"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            disabled: false,
            to: "/manufacturing",
            layout: "",
            name: translate(LanguageKey.v42_manufacturing_key),
            subPages: ["/manufacturing_order_detail"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.csm_team_key),
        state: "openCsmTeam",
        views: [
          {
            // Sensolus' own record of the customer relationship, so System only
            // (rows 113-114): the permission alone would show it to any login
            // that holds it.
            view: isSystem && hasPermission(UserPermission.MENU_ADMIN_CSM),
            disabled: false,
            to: "/csm_info",
            layout: "",
            name: translate(LanguageKey.csm_info_menu_key),
          },
          {
            view: isSystem && hasPermission(UserPermission.MENU_ADMIN_CSM),
            disabled: false,
            to: "/csm_comment",
            layout: "",
            name: translate(LanguageKey.csm_comment_menu_key),
            subPages: ["/csm_comment_create", "/csm_comment_update"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            disabled: false,
            to: "/internal_asset_classification",
            layout: "",
            name: translate(LanguageKey.internal_asset_classification_menu_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.products_and_firmware_key),
        state: "openSystemProductsFirmware",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              isSystem && hasPermission(UserPermission.MENU_ADMIN_TRACKER_PRODUCT),
            disabled: false,
            to: "/tracker_product",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKER_PRODUCT_description_key),
            subPages: ["/tracker_product_detail"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_PRODUCT_AUTO_UPGRADE),
            disabled: false,
            to: "/product_auto_upgrade",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRODUCT_AUTO_UPGRADE_description_key),
            subPages: ["/product_auto_upgrade_detail"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_PRODUCT_REGEXP_FILTER),
            disabled: false,
            to: "/product_regexp_filter",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_PRODUCT_REGEXP_FILTER_description_key),
            subPages: ["/product_regexp_filter_detail"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_TRACKER_FIRMWARE),
            disabled: false,
            to: "/tracker_firmware",
            layout: "",
            name: translate(LanguageKey.firmware_key),
            subPages: ["/tracker_firmware_detail"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.location_processing_key),
        state: "openSystemLocationProcessing",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_HARVESTED_CELLULAR),
            disabled: false,
            to: "/harvested_cellular",
            layout: "",
            name: translate(LanguageKey.harvested_cell_towers_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING),
            disabled: false,
            to: "/wifi_cellular_mismatch",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELL_TOWER_TROUBLESHOOTING_description_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.api_and_mcp_key),
        state: "openSystemApi",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_INTERNAL_API),
            disabled: false,
            to: "/internal-api",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTERNAL_API_description_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_AI_PROMPTS),
            disabled: false,
            to: "/ai-prompts",
            layout: "",
            name: translate(LanguageKey.ai_prompt_overview_key),
            subPages: ["/ai-prompt-update"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_AI_CHATS),
            disabled: false,
            to: "/ai-chats",
            layout: "",
            name: translate(LanguageKey.manage_ai_chats_key),
            subPages: ["/ai-chat-view"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.engineering_various_key),
        state: "openSystemPlatformNotifications",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRICELIST),
            disabled: false,
            to: "/price_lists",
            layout: "",
            name: translate(LanguageKey.price_list_key),
            subPages: ["/price_list_update", "/price_list_items"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_INTEGRATED_HELP),
            disabled: false,
            to: "/integrated_help",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTEGRATED_HELP_description_key),
            subPages: ["/integrated_help_update"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            disabled: false,
            to: "/doc-aliases",
            layout: "",
            name: translate(LanguageKey.doc_alias_overview_key),
          },
        
          // ---- org data at own level (no ?org= in the URL) ----
          {
          // Cross-org only concept — the same page in both modes.
          view:
            isSystem &&
            atOwnLevel &&
            (hasPermission(UserPermission.MENU_ADMIN_NOTIFICATION)),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_NOTIFICATION_description_key),
            to: "/notification",
            subPages: ["/notification_update"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            disabled: false,
            to: "/failed_trackers",
            layout: "",
            name: translate(LanguageKey.failed_trackers),
          },
          {
            view:
              isSystem &&
              atOwnLevel &&
              (hasFeature(FeatureKey.metar_weather_location) &&
                hasPermission(UserPermission.MENU_ADMIN_METAR_WEATHER_LOCATION)),
            disabled: false,
            layout: "",
            name: translate(
              LanguageKey.UserPermission_MENU_ADMIN_METAR_WEATHER_LOCATION_description_key
            ),
            to: "/metar_weather_location",
            subPages: ["/metar_weather_location_manage"],
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.MENU_ADMIN_INTERNAL_PLATFORM_PLAN),
            disabled: false,
            to: "/internal_plans",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_INTERNAL_PLATFORM_PLAN_description_key),
          },
          {
            view:
              isSystem &&
              hasPermission(UserPermission.OAUTH2_CLIENT_VIEW),
            disabled: false,
            to: "/oauth2_registered_client",
            layout: "",
            name: translate(LanguageKey.oauth2_client_management_key),
            subPages: [
              "/oauth2_registered_client_update",
              "/oauth2_registered_client_create",
            ],
          },
          {
            view:
              isSystem &&
              hasFeature(FeatureKey.loc_beacon) &&
              hasPermission(UserPermission.MENU_ADMIN_GEOLAYER_GENERATOR),
            disabled: false,
            to: "/geolayer_generator",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOLAYER_GENERATOR_description_key),
            subPages: ["/geolayer_generator_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.teamleader_integration_key),
        state: "openTeamleader",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_PRICE_BOOKS),
            disabled: false,
            to: "/customer",
            layout: "",
            name: translate(LanguageKey.customers_key),
            subPages: ["/customer_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.SYSTEM_ADMIN_VIEW),
            disabled: false,
            to: "/consolidated_customer",
            layout: "",
            name: translate(LanguageKey.consolidated_customers_key),
            subPages: ["/consolidated_customer_update"],
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.salesforce_integration_key),
        state: "openSalesforce",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_ACCOUNT),
            disabled: false,
            to: "/salesforce_account",
            layout: "",
            name: translate(LanguageKey.salesforce_accounts_key),
            subPages: ["/salesforce_account_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_CONTACT),
            disabled: false,
            to: "/salesforce_contact",
            layout: "",
            name: translate(LanguageKey.salesforce_contacts_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_LEAD),
            disabled: false,
            to: "/salesforce_lead",
            layout: "",
            name: translate(LanguageKey.salesforce_leads_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_OPPORTUNITY),
            disabled: false,
            to: "/salesforce_opportunity",
            layout: "",
            name: translate(LanguageKey.salesforce_opportunities_key),
            subPages: ["/salesforce_opportunity_line_items", "/salesforce_opportunity_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_QUOTE),
            disabled: false,
            to: "/salesforce_quote",
            layout: "",
            name: translate(LanguageKey.salesforce_quotes_key),
            subPages: ["/salesforce_quote_line_item", "/salesforce_quote_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_CHANNEL_PARTNER),
            disabled: false,
            to: "/salesforce_channel_partner",
            layout: "",
            name: translate(LanguageKey.salesforce_channel_partners_key),
            subPages: ["/salesforce_channel_partner_detail"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SALESFORCE_USER),
            disabled: false,
            to: "/salesforce_user",
            layout: "",
            name: translate(LanguageKey.salesforce_users_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.impartner_integration_key),
        state: "openImpartner",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_ACCOUNT),
            disabled: false,
            to: "/impartner_account",
            layout: "",
            name: translate(LanguageKey.impartner_accounts_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_CHANNEL_PARTNER),
            disabled: false,
            to: "/impartner_channel_partner",
            layout: "",
            name: translate(LanguageKey.impartner_channel_partners_key),
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_IMPARTNER_USER),
            disabled: false,
            to: "/impartner_user",
            layout: "",
            name: translate(LanguageKey.impartner_users_key),
          },
        ],
      },
      {
        collapse: true,
        name: translate(LanguageKey.communication_key),
        state: "openCommunication",
        // Part of the engineering block that closes the menu (STIC-15790).
        engineeringZone: true,
        views: [
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_SIGFOX_ACCOUNT),
            disabled: false,
            to: "/sigfox_account",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_SIGFOX_ACCOUNT_description_key),
            subPages: ["/sigfox_account_update", "/sigfox_account_device_type_info"],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_CELLULAR_ACCOUNT),
            disabled: false,
            to: "/cellular_account",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELLULAR_ACCOUNT_description_key),
            subPages: [
              "/cellular_account_manage",
              "/cellular_service_profile_operators",
              "/cellular_service_profile_manage",
              "/cellular_network_operator_chart",
            ],
          },
          {
            view:
              orgSelected?.organisationType === "SYSTEM" &&
              hasPermission(UserPermission.MENU_ADMIN_CELLULAR_SIM),
            disabled: false,
            to: "/cellular_sim",
            layout: "",
            name: translate(LanguageKey.UserPermission_MENU_ADMIN_CELLULAR_SIM_description_key),
            subPages: ["/cellular_sim_manage"],
          },
        ],
      },
    ];
  }, [
    loginInfo,
    orgSelected,
    organisation,
    isSystem,
    isPartnerLogin,
    isNormalLogin,
    atOwnLevel,
    homeMenuReachable,
    showDashboardGroup,
    // showAgentGroup is deliberately absent: the group is built with
    // `view: false` and useAgentMenuItems decides it, so the memo does not read it.
  ]);

  // The asynchronously filled copy of the table above. Same shape as the
  // cross-level menu's: `initialRoutes` is recomputed by the memo whenever a
  // visibility input changes, and the effect below re-adopts it while carrying
  // the fetched dashboard views across so they aren't refetched.
  const [routes, setRoutes] = useState<ExtendedMenuRoute[]>(initialRoutes);
  const [firstInit, setFirstInit] = useState(true);

  useEffect(() => {
    setRoutes((oldRoutes) => {
      if (oldRoutes === initialRoutes) return oldRoutes;
      // Carry the fetched entries over so a rebuild does not refetch them. The
      // Agents group brings its `view` as well: the memo rebuilds it as false
      // and only the fetch knows whether R8 lets it through.
      [DASHBOARD_GROUP_STATE, AGENT_GROUP_STATE].forEach((state) => {
        const loaded = oldRoutes.find((x) => x.state === state);
        if (!loaded?.views?.length) return;
        const target = initialRoutes.find((x) => x.state === state);
        if (!target) return;
        target.views = loaded.views;
        if (state === AGENT_GROUP_STATE) target.view = loaded.view;
      });
      return initialRoutes;
    });
  }, [initialRoutes]);

  // Favourited dashboards + "All dashboards", shared with the Home menu.
  // No `category: "dashboard"` on the entries: own level is the absence of
  // `?org=`, and NavList would append the stored report org to every link.
  useDashboardMenuItems(setRoutes, {
    skip: !showDashboardGroup,
    tagWithDashboardCategory: false,
    onApplied: () => setFirstInit(false),
  });

  useAgentMenuItems(setRoutes, {
    enabled: showAgentGroup,
    orgId: organisation?.id,
    onApplied: () => setFirstInit(false),
  });

  // Where own level opens the first time - the Admin button, the pill's own tab,
  // a partner's landing without Home: the organisation register for System and a
  // partner, the asset trackers for a customer (each falls back to the first item
  // shown).
  const ownLevelDefaultPath = isSystem || isPartnerLogin ? "/organization" : "/trackers";

  const [data, setData] = useState<OwnLevelMenuData>({
    type: MenuTypeConst.ADMIN,
    menuSource: MenuSourceConst.OWN,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: ownLevelDefaultPath,
  });

  const availibleMenuData = useAvailibleMenu(routes, ownLevelDefaultPath);

  useEffect(() => {
    // The dashboard entries load asynchronously and are appended to `routes`
    // after the first render, so `availibleMenuData` can still describe the
    // pre-async menu when it first arrives. Applying that flips `loading` to
    // false against a menu that does not yet register /all_dashboards or
    // /bi_dashboard/<id> — enough to bounce a login whose configured landing
    // page is a dashboard straight to the default page (the STIC-15267 shape).
    // Wait for the fetch to land AND for the computed menu to reflect it.
    if (firstInit) return;
    if (availibleMenuData.routes !== routes) return;
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData, firstInit]);

  useEffect(() => {
    if (custumInfo) {
      setSideBarColors(custumInfo);
    }
  }, [custumInfo]);

  useEffect(() => {
    // Staying at own level, the menu is the login's own whatever `org` says (absent,
    // the login's own id, or a record's owner on an own-exclusive route): nothing is
    // recomputed, so nothing would clear a `loading` set here, and SidebarBody's menu
    // resolution stalled on it - the Admin button from the Home report form left the
    // Home menu up on /organization.
    if (!(atOwnLevel && resolvedAtOwnLevel === true)) {
      setData((prev) => ({ ...prev, loading: true }));
    }
    setResolvedAtOwnLevel(atOwnLevel);
    if (atOwnLevel) {
      // Nothing to resolve: own level *is* the login's own organisation. The
      // entries that still test `orgSelected?.organisationType` — the System-only
      // pages outside the spreadsheet — depend on this being populated.
      setOrgSelected(loginInfo.organisation ?? null);
      setResolvedOrgId(organisation?.id ?? null);
    } else if (orgId) {
      if (
        loginInfo.organisation?.id !== orgId &&
        hasPermission(UserPermission.ORGANIZATION_VIEW)
      ) {
        organizationClient
          .findOne({ id: orgId })
          .then(({ data: org }) => setOrgSelected(org))
          .catch(() => setOrgSelected(null))
          .finally(() => setResolvedOrgId(orgId));
      } else {
        setOrgSelected(loginInfo.organisation ?? null);
        setResolvedOrgId(orgId);
      }
    } else {
      setOrgSelected(null);
      setResolvedOrgId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, atOwnLevel]);

  // The menu paths are derived from `orgSelected`, which is updated
  // asynchronously after `orgId` changes. Until it catches up, the returned
  // paths still reflect the PREVIOUS org — report loading so consumers
  // (SidebarBody) wait for the recompute instead of routing on stale paths
  // (which would e.g. bounce a freshly-set ?org= deep link to the default
  // admin page because the requested route isn't yet "visible").
  const orgNotReady = !atOwnLevel && orgId != null && resolvedOrgId !== orgId;

  // Same staleness, other axis: EVERY entry in this menu is gated on
  // `atOwnLevel`, so the moment the level flips the whole menu changes shape.
  // `routes` is a useMemo and updates in the same render, but `useAvailibleMenu`
  // derives the path maps in a `useEffect`, so `data.availableMenuPaths` is one
  // commit behind — and `data.loading` is still `false` from the previous level.
  //
  // Without this, going back from a sliced detail to an own-level list bounced to
  // /no_access: SidebarBody's loading guard passed, it resolved the own menu
  // (the URL had no org, so the level WAS own), then looked the list up in the
  // cross-level path map, where every own entry was `view: false`. The sidebar
  // rendered correctly a commit later — the redirect had already happened.
  const levelNotReady = resolvedAtOwnLevel !== atOwnLevel;

  return orgNotReady || levelNotReady ? { ...data, loading: true } : data;
};

export default useOwnLevelMenu;
